import { Injectable } from '@angular/core';
import { CustomOrderEntry } from '@cart/const';
import { ActiveCartService, CartActions } from '@cart/data-access';
import { findEntryByGuid } from '@cart/utils';
import { CityActions } from '@city/data-access';
import { CityService } from '@city/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { OrderEntry } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  CartAnalyticsService,
  CheckoutAnalyticsGaV4Service,
  ProductListCartAnalyticsService,
  RetailRocketService,
} from '../services';

@Injectable()
export class CartAnalyticsEffects {
  addCartEntrySuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.CART_ADD_ENTRY_SUCCESS),
        map((action: CartActions.CartAddEntrySuccess) => action.payload.entry),
        withLatestFrom(this.cityService.activeBaseStoreCode$),
        map(([entry, activeBaseStoreCode]: [OrderEntry, string]) => {
          this.productListCartAnalyticsService.updateMapForEntry(entry);

          this.cartAnalyticsService.onAddToCart(entry);
          this.retailRocketService.onAddToCart({ entry, activeBaseStoreCode });

          return;
        }),
      ),
    { dispatch: false },
  );

  updateCartEntrySuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.CART_UPDATE_ENTRY_SUCCESS),
        map(({ payload }: CartActions.CartUpdateEntrySuccess) => payload),
        withLatestFrom(this.activeCartService.getEntries() as Observable<CustomOrderEntry[]>),
        map(
          ([{ entryNumber: positionGuid, quantity }, entries]: [
            CartActions.CartUpdateEntrySuccess['payload'],
            CustomOrderEntry[],
          ]) => ({
            quantity: quantity || 0,
            entry: findEntryByGuid(entries, positionGuid),
          }),
        ),
        filter(({ entry }) => Boolean(entry)),
        withLatestFrom(this.cityService.activeBaseStoreCode$),
        map(([{ entry, quantity }, activeBaseStoreCode]: [{ entry: OrderEntry; quantity: number }, string]) => ({
          prevEntry: { ...entry },
          currentEntry: { ...entry, quantity },
          activeBaseStoreCode,
        })),
        map(({ prevEntry, currentEntry, activeBaseStoreCode }) => {
          if (currentEntry.quantity > prevEntry.quantity) {
            this.cartAnalyticsService.onAddToCart({
              ...currentEntry,
              quantity: currentEntry.quantity - prevEntry.quantity,
            });
            this.retailRocketService.onAddToCart({ entry: currentEntry, activeBaseStoreCode });
          } else if (currentEntry.quantity < prevEntry.quantity) {
            this.cartAnalyticsService.onRemoveFromCart({
              ...currentEntry,
              quantity: prevEntry.quantity - currentEntry.quantity,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  removeCartEntrySuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.CART_REMOVE_ENTRY_SUCCESS),
        map((action: CartActions.CartRemoveEntrySuccess) => action.payload.entryNumber),
        withLatestFrom(this.activeCartService.getEntries() as Observable<CustomOrderEntry[]>),
        map(([entryNumber, entries]) => findEntryByGuid(entries, entryNumber)),
        filter((entry: OrderEntry) => Boolean(entry)),
        map((entry: OrderEntry) => {
          this.cartAnalyticsService.onRemoveFromCart(entry);

          return;
        }),
      ),
    { dispatch: false },
  );

  removeAllCartEntriesSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.REMOVE_ALL_CART_ENTRIES_SUCCESS),
        withLatestFrom(this.activeCartService.getEntries()),
        map(([, entries]) => entries),
        map((entries: OrderEntry[]) => {
          for (const entry of entries) {
            this.cartAnalyticsService.onRemoveFromCart(entry);
          }

          return;
        }),
      ),
    { dispatch: false },
  );

  cleanMapAnalitic$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CartActions.CART_REMOVE_ENTRY_SUCCESS,
          CartActions.REMOVE_ALL_CART_ENTRIES_SUCCESS,
          CartActions.SET_ACTIVE_CART_ID,
          CityActions.SET_ACTIVE_BASE_STORE,
        ),
        tap(() => {
          this.productListCartAnalyticsService.cleanMapAnalitycData();
        }),
      ),
    { dispatch: false },
  );

  goToNonEmptyCartPage$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(
          ({
            payload: {
              routerState: { semanticRoute, queryParams },
            },
          }) => semanticRoute === 'cart' && !queryParams['cloneFrom'],
        ),
        map(() => {
          this.activeCartService
            .getEntries()
            .pipe(
              take(1),
              filter((entries: OrderEntry[]) => entries.length > 0),
              tap((entries: OrderEntry[]) => {
                this.checkoutAnalyticsGaV4Service.onGoToNonEmptyCartPage(entries);
              }),
            )
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private activeCartService: ActiveCartService,
    private cartAnalyticsService: CartAnalyticsService,
    private productListCartAnalyticsService: ProductListCartAnalyticsService,
    private retailRocketService: RetailRocketService,
    private cityService: CityService,
    private checkoutAnalyticsGaV4Service: CheckoutAnalyticsGaV4Service,
  ) {}
}
